import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, DollarSign, Wallet, LogOut, Loader2, Plus, Trash2, Send,
  RefreshCw, ExternalLink, AlertCircle, CheckCircle2, Hourglass, XCircle,
  TrendingUp, Settings, Sparkles, ChevronRight, ChevronLeft, Copy, CopyCheck,
  Trophy, Crown, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  clipperMe, clipperUpdateMe, clipperListClips, clipperSubmitClip,
  clipperDeleteClip, clipperListWithdrawals, clipperRequestWithdrawal,
  getPublicSettings, setClipperToken, getStoredClipperToken, apiErrMsg,
  clipperLeaderboard,
} from '../../lib/api';
import { useT } from '../../lib/i18n';
import { LanguageToggle } from '../../components/LanguageToggle';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

const PLATFORM_LABEL = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

function formatCurrency(n) {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
}
function formatNumber(n) {
  return Number(n || 0).toLocaleString();
}
function shortDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function ClipDashboardPage() {
  const t = useT();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [settings, setSettings] = useState(null);
  const [clips, setClips] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('clips');

  const refresh = useCallback(async () => {
    try {
      const [meRes, cl, ws, lb] = await Promise.all([
        clipperMe(),
        clipperListClips(),
        clipperListWithdrawals(),
        clipperLeaderboard(5).catch(() => ({ items: [] })),
      ]);
      setMe(meRes);
      setClips(cl.items || []);
      setWithdrawals(ws.items || []);
      setLeaderboard(lb.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setClipperToken(null);
        navigate('/clip/login', { replace: true });
        return;
      }
      toast.error(apiErrMsg(e, 'Failed to load dashboard'));
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!getStoredClipperToken()) {
      navigate('/clip/login', { replace: true });
      return;
    }
    (async () => {
      try { setSettings(await getPublicSettings()); } catch { /* ignore */ }
      await refresh();
    })();
  }, [navigate, refresh]);

  function logout() {
    setClipperToken(null);
    toast.success('Logged out');
    navigate('/clip/login', { replace: true });
  }

  if (loading || !me) {
    return (
      <main className="min-h-screen bg-[hsl(0_0%_4%)] text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(43_92%_56%)]" />
      </main>
    );
  }

  const rate = settings?.clip_rate_usd_per_1m ?? 40;
  const minWith = settings?.clip_min_withdrawal_usd ?? 10;
  const tgSrc = settings?.clip_source_telegram_link || 'https://t.me/alveefxcliping';

  return (
    <main className="min-h-screen text-white bg-[hsl(0_0%_4%)] relative noise-overlay" data-testid="clip-dashboard">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 0% 0%, rgba(245,185,66,0.10), transparent 70%), radial-gradient(40% 40% at 100% 100%, rgba(225,29,46,0.10), transparent 70%)',
        }}
      />
      <header className="relative z-10 border-b border-white/5">
        <div className="container-x py-3.5 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/clip" className="flex items-center gap-2.5">
            <img src={SITE_LOGO} alt="AlveeFx" className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]" />
            <span className="font-display uppercase tracking-[0.18em] text-sm sm:text-base">AlveeFx</span>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] px-2 py-0.5 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)]">Clipper</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle className="hidden sm:inline-flex" />
            <a href={tgSrc} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="h-9 border-white/10 bg-white/[0.02] gap-1.5" data-testid="header-tg-source">
                <Send className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t('Source videos')}</span>
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={logout} className="h-9 border-white/10 bg-white/[0.02] gap-1.5" data-testid="logout">
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t('Logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative z-10 py-8">
        <div className="container-x max-w-6xl mx-auto space-y-6">
          {/* Welcome strip */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('Welcome back')}</div>
              <div className="font-display text-2xl sm:text-3xl mt-0.5">{me.name || me.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle className="sm:hidden" />
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.22em] border-white/10 bg-white/[0.02]">
                ${rate}/1M views · USDT BEP-20
              </Badge>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat Icon={Eye}        label={t('Total views')}      value={formatNumber(me.total_views)} testid="stat-views" />
            <Stat Icon={DollarSign} label={t('Total earned')}     value={formatCurrency(me.total_earned_usd)} testid="stat-earned" />
            <Stat Icon={Wallet}     label={t('Pending balance')}  value={formatCurrency(me.pending_balance_usd)} gold testid="stat-pending" />
            <Stat Icon={TrendingUp} label={t('Paid out')}         value={formatCurrency(me.total_paid_out_usd)} testid="stat-paid" />
          </div>
          {Number(me.held_balance_usd || 0) > 0 && (
            <div className="rounded-md border border-[rgba(225,29,46,0.45)] bg-[rgba(225,29,46,0.06)] px-4 py-3 flex items-center gap-3">
              <div className="text-2xl">🔴</div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(0_72%_75%)]">
                  {formatCurrency(me.held_balance_usd)} {t('on hold — pending review')}
                </div>
                <div className="text-[12px] text-white/70 mt-0.5">
                  Some of your clips have abnormally low engagement (like:view ratio). Held earnings will be released
                  after admin review. If your traffic is 100% organic, please contact support with proof.
                </div>
              </div>
            </div>
          )}

          {/* Source videos + Leaderboard side by side */}
          <div className="grid gap-4 lg:grid-cols-[1fr,1.2fr]">
            <SourceVideosCard tgLink={tgSrc} rate={rate} />
            <LeaderboardCard items={leaderboard} myId={me.id} />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white/[0.02] border border-white/10 p-1 gap-1" data-testid="dashboard-tabs">
              <TabsTrigger value="clips" data-testid="tab-clips" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                {t('Clips')} ({clips.length})
              </TabsTrigger>
              <TabsTrigger value="withdraw" data-testid="tab-withdraw" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                {t('Withdraw earnings')}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                {t('Withdrawal history').replace(' history', '')}
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                <Settings className="h-3.5 w-3.5 mr-1" /> {t('Profile')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="clips" className="mt-5">
              <ClipsTab clips={clips} onRefresh={refresh} rate={rate} settings={settings} me={me} />
            </TabsContent>

            <TabsContent value="withdraw" className="mt-5">
              <WithdrawTab me={me} minWith={minWith} onSubmitted={refresh} />
            </TabsContent>

            <TabsContent value="history" className="mt-5">
              <HistoryTab withdrawals={withdrawals} />
            </TabsContent>

            <TabsContent value="profile" className="mt-5">
              <ProfileTab me={me} onSaved={refresh} />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
}

function Stat({ Icon, label, value, gold, testid }) {
  return (
    <Card className={'card-surface ' + (gold ? 'border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.04)]' : '')}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <Icon className={'h-3.5 w-3.5 ' + (gold ? 'text-[hsl(43_92%_56%)]' : '')} />
          {label}
        </div>
        <div className={'font-display text-2xl sm:text-3xl mt-1 tabular-nums ' + (gold ? 'text-[hsl(43_92%_56%)]' : 'text-white')} data-testid={testid}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

// ============== SOURCE VIDEOS CTA CARD ==============
function SourceVideosCard({ tgLink, rate }) {
  return (
    <Card className="card-surface border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.04)] overflow-hidden relative" data-testid="source-videos-card">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgba(245,185,66,0.20), transparent 70%)' }}
      />
      <CardContent className="p-5 relative">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[rgba(245,185,66,0.12)] border border-[hsl(43_92%_56%)]/40 flex items-center justify-center shrink-0">
            <PlayCircle className="h-5 w-5 text-[hsl(43_92%_56%)]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] font-semibold">Step 1 · Source</div>
            <div className="font-display text-lg leading-tight mt-0.5">Get fresh AlveeFx videos</div>
            <p className="text-[12px] text-[hsl(0_0%_82%)] leading-relaxed mt-1">
              Join our Telegram channel to download the latest official AlveeFx clips, raw footage, and brand assets. Free to remix. Earn ${rate}/1M views.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <a href={tgLink} target="_blank" rel="noreferrer" data-testid="source-videos-cta" className="inline-flex">
            <Button className="cta-sheen border-0 h-10 px-5 font-semibold gap-2">
              <Send className="h-4 w-4" /> Open Telegram channel
            </Button>
          </a>
          <span className="text-[11px] text-muted-foreground font-mono truncate max-w-[220px]" title={tgLink}>
            {tgLink.replace('https://', '')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ============== LEADERBOARD CARD ==============
function LeaderboardCard({ items, myId }) {
  return (
    <Card className="card-surface" data-testid="leaderboard-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[hsl(43_92%_56%)]" />
            <div className="font-display text-lg">Top earners</div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">live · all time</span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Top 5</span>
        </div>
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-white/10 bg-white/[0.02] py-6 text-center" data-testid="leaderboard-empty">
            <Sparkles className="h-6 w-6 text-[hsl(43_92%_56%)] mx-auto mb-1.5" />
            <div className="text-sm font-semibold text-white">Be the first on the board</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Submit a clip and climb the rankings.</div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((row) => {
              const isMe = row.id === myId; // backend masks names but if it ever exposes id, highlight
              const rankIconBg =
                row.rank === 1 ? 'bg-[rgba(245,185,66,0.18)] border-[hsl(43_92%_56%)]/60 text-[hsl(43_92%_56%)]' :
                row.rank === 2 ? 'bg-white/[0.08] border-white/30 text-white' :
                row.rank === 3 ? 'bg-[rgba(205,127,50,0.16)] border-[rgba(205,127,50,0.55)] text-[hsl(28_45%_60%)]' :
                'bg-white/[0.04] border-white/10 text-muted-foreground';
              return (
                <div key={row.rank} className={'flex items-center gap-3 py-2.5 ' + (isMe ? 'bg-[rgba(245,185,66,0.04)] -mx-2 px-2 rounded' : '')} data-testid={`leaderboard-row-${row.rank}`}>
                  <div className={'h-8 w-8 rounded-full border flex items-center justify-center shrink-0 font-display text-sm tabular-nums ' + rankIconBg}>
                    {row.rank === 1 ? <Crown className="h-4 w-4" /> : `#${row.rank}`}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{row.display_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {row.country || 'Global'} · {formatNumber(row.total_views)} views
                    </div>
                  </div>
                  <div className="font-display text-lg text-[hsl(43_92%_56%)] tabular-nums shrink-0">
                    {formatCurrency(row.total_earned_usd)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============== CLIPS TAB ==============
function ClipsTab({ clips, onRefresh, rate, settings, me }) {
  const t = useT();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ platform: 'tiktok', url: '', title: '' });
  const [openForm, setOpenForm] = useState(false);

  const requiredTokens = (settings?.clip_required_attribution_tokens) || [];
  const platMap = settings?.clip_platform_attribution || {};
  const platformTokens = Array.isArray(platMap[form.platform]) ? platMap[form.platform] : [];
  const requireHandle = settings?.clip_require_handle_match !== false;
  const sh = me?.social_handles || { tiktok: [], instagram: [], facebook: [] };
  const currentHandles = Array.isArray(sh[form.platform]) ? sh[form.platform] : [];
  const missingHandles = requireHandle && currentHandles.length === 0;

  function detectPlatform(url) {
    const u = (url || '').toLowerCase();
    if (u.includes('tiktok.com') || u.includes('vm.tiktok.com')) return 'tiktok';
    if (u.includes('instagram.com') || u.includes('instagr.am')) return 'instagram';
    if (u.includes('facebook.com') || u.includes('fb.watch') || u.includes('fb.com')) return 'facebook';
    return null;
  }

  async function submitClip(e) {
    e?.preventDefault?.();
    if (!form.url.trim()) { toast.error('Please paste your clip URL'); return; }
    const detected = detectPlatform(form.url);
    if (detected && detected !== form.platform) {
      setForm((f) => ({ ...f, platform: detected }));
    }
    setSubmitting(true);
    const loadingId = toast.loading('Verifying your clip — this can take 20–60 seconds…');
    try {
      await clipperSubmitClip({
        platform: detected || form.platform,
        url: form.url.trim(),
        title: form.title.trim(),
      });
      toast.dismiss(loadingId);
      toast.success('Clip verified & submitted ✓');
      setForm({ platform: 'tiktok', url: '', title: '' });
      setOpenForm(false);
      onRefresh();
    } catch (err) {
      toast.dismiss(loadingId);
      toast.error(apiErrMsg(err, "Failed to submit"));
    } finally { setSubmitting(false); }
  }

  async function removeClip(id) {
    if (!window.confirm('Remove this clip? Already-earned amount stays on your balance.')) return;
    try {
      await clipperDeleteClip(id);
      toast.success('Clip removed');
      onRefresh();
    } catch (err) {
      toast.error(apiErrMsg(err, "Failed to remove"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl">{t('Clips')}</h2>
          <div className="text-xs text-muted-foreground">{t('per 1M views')}: ${rate}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} className="h-9 border-white/10 bg-white/[0.02] gap-1.5" data-testid="refresh-clips">
            <RefreshCw className="h-3.5 w-3.5" /> {t('Refresh')}
          </Button>
          <Button size="sm" onClick={() => setOpenForm((v) => !v)} className="cta-sheen border-0 h-9 gap-1.5" data-testid="open-submit">
            <Plus className="h-3.5 w-3.5" /> {t('Submit clip')}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {openForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <Card className="card-surface" data-testid="submit-form">
              <CardContent className="p-5 space-y-3">
                {/* Anti-fraud requirements panel */}
                <div className="rounded-md border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] p-3 space-y-1.5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] font-semibold">
                    Before you submit — clip requirements
                  </div>
                  <ul className="text-[12px] text-white/80 space-y-1 list-disc pl-4">
                    {requireHandle && (
                      <li>
                        Clip <b>must be posted from your own account</b>
                        {currentHandles.length > 0 ? (
                          <> (registered: {currentHandles.map((h) => `@${h}`).join(', ')})</>
                        ) : (
                          <> — <span className="text-[hsl(0_72%_72%)]">no {form.platform} handle registered yet</span></>
                        )}
                      </li>
                    )}
                    {requiredTokens.length > 0 && (
                      <li>
                        Caption must include <b>at least ONE</b> of: {requiredTokens.map((t, i) => (
                          <span key={i} className="font-mono text-[hsl(43_92%_72%)]">{t}{i < requiredTokens.length - 1 ? ' · ' : ''}</span>
                        ))}
                      </li>
                    )}
                    {platformTokens.length > 0 && (
                      <li>
                        <span className="uppercase text-[10px] tracking-[0.18em] text-[hsl(43_92%_72%)]">{form.platform}-only:</span>{' '}
                        Caption must ALSO include: {platformTokens.map((t, i) => (
                          <span key={i} className="font-mono text-[hsl(43_92%_72%)]">{t}{i < platformTokens.length - 1 ? ' + ' : ''}</span>
                        ))}
                      </li>
                    )}
                    <li>Post must be public (not private / age-restricted).</li>
                  </ul>
                  {missingHandles && (
                    <div className="text-[11px] text-[hsl(0_72%_72%)] mt-1">
                      Add your {form.platform} handle in the <b>Profile</b> tab first, or your submission will be rejected.
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Platform</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {Object.entries(PLATFORM_LABEL).map(([key, label]) => (
                      <button key={key} type="button" onClick={() => setForm((f) => ({ ...f, platform: key }))}
                        data-testid={`platform-${key}`}
                        className={
                          'rounded-md border px-3 py-2 text-sm transition-colors ' +
                          (form.platform === key
                            ? 'border-[hsl(43_92%_56%)] bg-[rgba(245,185,66,0.08)] text-white'
                            : 'border-white/10 bg-white/[0.02] hover:border-white/20 text-muted-foreground')
                        }>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Public URL</Label>
                  <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://www.tiktok.com/@you/video/..."
                    data-testid="submit-url"
                    onBlur={() => {
                      const d = detectPlatform(form.url);
                      if (d) setForm((f) => ({ ...f, platform: d }));
                    }}
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Must be a public post that contains AlveeFx content. Each URL can only be claimed once.
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Title (optional)</Label>
                  <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. 60s — Forex Risk Management" data-testid="submit-title" />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setOpenForm(false)} className="border-white/10 bg-white/[0.02]" data-testid="submit-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" disabled={submitting} onClick={submitClip} className="cta-sheen border-0 gap-1.5" data-testid="submit-confirm">
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Submit
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {clips.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="p-10 text-center">
            <Sparkles className="h-8 w-8 text-[hsl(43_92%_56%)] mx-auto mb-2" />
            <div className="font-display text-lg">No clips yet</div>
            <p className="text-xs text-muted-foreground mt-1">Post a clip on TikTok / Instagram / Facebook then paste its URL here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {clips.map((c) => <ClipRow key={c.id} clip={c} onRemove={removeClip} />)}
        </div>
      )}
    </div>
  );
}

function ClipRow({ clip, onRemove }) {
  const platformBadge = {
    tiktok:    'bg-[rgba(36,225,225,0.10)] border-[rgba(36,225,225,0.40)] text-[hsl(180_60%_70%)]',
    instagram: 'bg-[rgba(225,29,146,0.10)] border-[rgba(225,29,146,0.40)] text-[hsl(322_60%_70%)]',
    facebook:  'bg-[rgba(36,108,225,0.10)] border-[rgba(36,108,225,0.40)] text-[hsl(212_70%_70%)]',
  }[clip.platform] || '';

  const statusBadge = clip.status === 'active'
    ? 'border-white/10 text-muted-foreground'
    : clip.status === 'blocked'
    ? 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]'
    : 'border-white/10 text-muted-foreground';

  return (
    <Card className="card-surface" data-testid={`clip-row-${clip.id}`}>
      <CardContent className="p-4 flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={'text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border font-semibold ' + platformBadge}>
              {PLATFORM_LABEL[clip.platform] || clip.platform}
            </span>
            <span className={'text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border ' + statusBadge}>
              {clip.status}
            </span>
            <a href={clip.url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(43_92%_72%)] hover:underline truncate inline-flex items-center gap-1" data-testid="clip-link">
              <span className="truncate max-w-[260px] sm:max-w-[420px]">{clip.url}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>
          {clip.title && <div className="text-sm mt-1 truncate">{clip.title}</div>}
          {clip.engagement_flag === 'hard' && clip.engagement_review !== 'confirmed_organic' && (
            <div className="mt-2 rounded-md border border-[rgba(225,29,46,0.45)] bg-[rgba(225,29,46,0.06)] px-2.5 py-1.5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[hsl(0_72%_75%)]">
                🔴 Earnings on hold — suspicious engagement
              </div>
              <div className="text-[11px] text-white/70 mt-0.5">
                Your like/view ratio ({clip.engagement_like_rate_pct ?? '?'}%) looks abnormally low. This is a
                common signature of bought/bot views (SMM panels). Earnings from this clip are held from
                withdrawal until an admin reviews. If your views are 100% organic, contact support.
              </div>
            </div>
          )}
          {clip.engagement_flag === 'soft' && clip.engagement_review !== 'confirmed_organic' && (
            <div className="mt-2 text-[11px] text-[hsl(43_92%_72%)]">
              🟡 Low engagement warning: like rate {clip.engagement_like_rate_pct}%
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
            Last scan: <span className="text-white/80">{shortDate(clip.last_scraped_at)}</span>
            {clip.scrape_error && (
              <span className="ml-2 text-[hsl(0_72%_72%)]">· {clip.scrape_error}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Views</div>
            <div className="font-display text-xl tabular-nums">{formatNumber(clip.current_views)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Earned</div>
            <div className="font-display text-xl text-[hsl(43_92%_56%)] tabular-nums">{formatCurrency(clip.earned_usd)}</div>
          </div>
          <button type="button" onClick={() => onRemove(clip.id)}
            className="h-8 w-8 rounded-md border border-white/10 bg-white/[0.02] hover:bg-[rgba(225,29,46,0.10)] hover:border-[rgba(225,29,46,0.55)] hover:text-[hsl(0_72%_70%)] transition-colors flex items-center justify-center"
            data-testid={`clip-remove-${clip.id}`} title="Remove clip">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============== WITHDRAW TAB ==============
function WithdrawTab({ me, minWith, onSubmitted }) {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState(me.usdt_bep20_address || '');
  const [submitting, setSubmitting] = useState(false);
  const pending = Number(me.pending_balance_usd || 0);

  async function submit() {
    const amt = Number(amount);
    if (!amt || amt < minWith) { toast.error(`Minimum withdrawal is $${minWith}`); return; }
    if (amt > pending + 0.001) { toast.error(`Available balance is ${formatCurrency(pending)}`); return; }
    if (!address.startsWith('0x') || address.length !== 42) {
      toast.error('Please enter a valid USDT BEP-20 address (starts with 0x, 42 chars)'); return;
    }
    setSubmitting(true);
    try {
      await clipperRequestWithdrawal({ amount_usd: amt, usdt_bep20_address: address });
      toast.success('Withdrawal request submitted!');
      setAmount('');
      onSubmitted();
    } catch (err) {
      toast.error(apiErrMsg(err, "Failed to submit"));
    } finally { setSubmitting(false); }
  }

  const canSubmit = pending >= minWith;

  return (
    <Card className="card-surface" data-testid="withdraw-form">
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl">Request a withdrawal</h2>
            <div className="text-xs text-muted-foreground mt-0.5">Minimum: ${minWith} · USDT on BNB Smart Chain (BEP-20)</div>
          </div>
          <div className="rounded-md border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)] px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Available</div>
            <div className="font-display text-xl text-[hsl(43_92%_56%)] tabular-nums">{formatCurrency(pending)}</div>
          </div>
        </div>

        {!canSubmit && (
          <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5 flex items-start gap-2 text-xs text-muted-foreground" data-testid="withdraw-below-min">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-[hsl(43_92%_56%)] shrink-0" />
            You need at least ${minWith} pending to request a withdrawal. Keep clipping!
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Amount (USD)</Label>
            <Input type="number" min={minWith} max={pending} step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`Min ${minWith}`} disabled={!canSubmit} data-testid="withdraw-amount" />
            <div className="flex gap-1.5 mt-1.5">
              {[minWith, 25, 50, 100].filter((v) => v <= pending).slice(0, 4).map((v) => (
                <button key={v} type="button" onClick={() => setAmount(String(v))}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-[hsl(43_92%_56%)]/40 text-muted-foreground hover:text-white">
                  ${v}
                </button>
              ))}
              {pending >= minWith && (
                <button type="button" onClick={() => setAmount(String(pending.toFixed(2)))}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-[hsl(43_92%_56%)]/40 text-[hsl(43_92%_72%)] hover:text-white"
                  data-testid="withdraw-max">
                  Max ${pending.toFixed(2)}
                </button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">USDT BEP-20 address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="0x..." className="font-mono text-xs" disabled={!canSubmit} data-testid="withdraw-address" />
            <div className="text-[10px] text-muted-foreground mt-1">
              ⚠ Make sure your wallet supports <span className="text-[hsl(43_92%_72%)]">BEP-20 / BNB Smart Chain</span>. Wrong network = lost funds.
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button size="lg" disabled={!canSubmit || submitting} onClick={submit}
            className="cta-sheen border-0 h-11 px-6 font-semibold gap-2 w-full sm:w-auto" data-testid="withdraw-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            Request withdrawal
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============== HISTORY TAB ==============
function HistoryTab({ withdrawals }) {
  if (withdrawals.length === 0) {
    return (
      <Card className="card-surface">
        <CardContent className="p-10 text-center">
          <Hourglass className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <div className="font-display text-lg">No withdrawals yet</div>
          <p className="text-xs text-muted-foreground mt-1">Your past requests will show up here.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {withdrawals.map((w) => <WithdrawalRow key={w.id} w={w} />)}
    </div>
  );
}

function WithdrawalRow({ w }) {
  const statusMap = {
    pending: { Icon: Hourglass, cls: 'border-white/10 text-muted-foreground', label: 'Pending review' },
    approved: { Icon: ChevronRight, cls: 'border-[rgba(108,176,255,0.55)] text-[hsl(212_80%_75%)]', label: 'Approved · sending' },
    paid: { Icon: CheckCircle2, cls: 'border-[rgba(70,200,120,0.55)] text-[hsl(140_70%_70%)]', label: 'Paid' },
    rejected: { Icon: XCircle, cls: 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]', label: 'Rejected' },
  };
  const sm = statusMap[w.status] || statusMap.pending;
  const Icon = sm.Icon;
  return (
    <Card className="card-surface" data-testid={`withdraw-row-${w.id}`}>
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={'h-9 w-9 rounded-full flex items-center justify-center border ' + sm.cls + ' bg-white/[0.02]'}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg tabular-nums">{formatCurrency(w.amount_usd)}</div>
            <div className="text-[11px] text-muted-foreground truncate">{w.usdt_bep20_address}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={'text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border ' + sm.cls}>
            {sm.label}
          </span>
          <div className="text-[10px] text-muted-foreground hidden sm:block">{shortDate(w.requested_at)}</div>
        </div>
        {w.txhash && (
          <div className="basis-full text-[11px] text-muted-foreground font-mono mt-1 break-all">
            tx: {w.txhash}
          </div>
        )}
        {w.admin_notes && (
          <div className="basis-full text-[11px] text-muted-foreground mt-1">
            <span className="uppercase tracking-[0.18em]">Note:</span> {w.admin_notes}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============== PROFILE TAB ==============
function ProfileTab({ me, onSaved }) {
  const t = useT();
  const [form, setForm] = useState({
    name: me.name || '',
    country: me.country || '',
    telegram_handle: me.telegram_handle || '',
    usdt_bep20_address: me.usdt_bep20_address || '',
  });
  // Social handles: object with tiktok/instagram/facebook arrays
  const initSh = me.social_handles || { tiktok: [], instagram: [], facebook: [] };
  const [handles, setHandles] = useState({
    tiktok: Array.isArray(initSh.tiktok) ? initSh.tiktok.join(', ') : '',
    instagram: Array.isArray(initSh.instagram) ? initSh.instagram.join(', ') : '',
    facebook: Array.isArray(initSh.facebook) ? initSh.facebook.join(', ') : '',
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  function splitHandles(raw) {
    if (!raw) return [];
    return raw
      .split(/[\s,\n]+/)
      .map((h) => h.trim())
      .filter(Boolean)
      .slice(0, 10);
  }

  async function save() {
    setSaving(true);
    try {
      await clipperUpdateMe({
        ...form,
        social_handles: {
          tiktok: splitHandles(handles.tiktok),
          instagram: splitHandles(handles.instagram),
          facebook: splitHandles(handles.facebook),
        },
      });
      toast.success('Profile updated');
      onSaved();
    } catch (err) {
      toast.error(apiErrMsg(err, "Save failed"));
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <Card className="card-surface" data-testid="profile-form">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div>
            <h2 className="font-display text-xl">{t('Your profile')}</h2>
            <div className="text-xs text-muted-foreground mt-0.5">{t('Email')}: <span className="text-white/80">{me.email}</span></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('Name')}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('Country')}</Label>
              <Input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} placeholder="e.g. Bangladesh" data-testid="profile-country" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('Telegram handle')}</Label>
              <Input value={form.telegram_handle} onChange={(e) => setForm((f) => ({ ...f, telegram_handle: e.target.value }))} placeholder="@yourhandle" data-testid="profile-tg" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('USDT BEP-20 address')}</Label>
              <div className="flex gap-2">
                <Input value={form.usdt_bep20_address} onChange={(e) => setForm((f) => ({ ...f, usdt_bep20_address: e.target.value }))} placeholder="0x..." className="font-mono text-xs" data-testid="profile-wallet" />
                {form.usdt_bep20_address && (
                  <button type="button" onClick={() => {
                    navigator.clipboard.writeText(form.usdt_bep20_address);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }} className="shrink-0 h-10 w-10 rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] flex items-center justify-center">
                    {copied ? <CopyCheck className="h-4 w-4 text-[hsl(140_70%_60%)]" /> : <Copy className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Social handles section — powers anti-fraud verification */}
          <div className="pt-4 border-t border-white/10">
            <div className="mb-3">
              <h3 className="font-display text-base">{t('Social handles')}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('Add the accounts you post clips from. We use these to verify each submitted clip is actually yours. You can add multiple handles per platform (separate with commas).')}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">TikTok</Label>
                <Input
                  value={handles.tiktok}
                  onChange={(e) => setHandles((h) => ({ ...h, tiktok: e.target.value }))}
                  placeholder="@your_tiktok, @second_account"
                  data-testid="profile-handles-tiktok"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Instagram</Label>
                <Input
                  value={handles.instagram}
                  onChange={(e) => setHandles((h) => ({ ...h, instagram: e.target.value }))}
                  placeholder="@your_ig, @second_ig"
                  data-testid="profile-handles-instagram"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Facebook</Label>
                <Input
                  value={handles.facebook}
                  onChange={(e) => setHandles((h) => ({ ...h, facebook: e.target.value }))}
                  placeholder="page.username, other.page"
                  data-testid="profile-handles-facebook"
                />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Tip: You can paste a full URL (e.g. <span className="font-mono text-white/70">tiktok.com/@myuser</span>) — we&apos;ll extract the handle automatically.
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={save} disabled={saving} className="cta-sheen border-0 h-10 px-5 font-semibold gap-2" data-testid="profile-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronLeft className="h-4 w-4 rotate-180" />}
              {t('Save changes')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
